/**
 * Singbac Incorporation Post — 문서별 [[Signers]] 컨텍스트.
 *
 * Aura(incorporation-pre)의 sgPreSignersContext 를 복제하되, Singbac 은 서명 블록이
 * 필요한 문서가 DRIW(First Board Resolutions)의 이사회 서명 한 곳뿐이라 그 키만 둔다.
 * 엔티티는 이미 설립돼 있지만(post-incorporation), officer API 대신 이사 성명(client /
 * resident director)으로 시드 + 수동 추가만 사용한다 (Aura Pre 패턴과 동일).
 */
import { inject, provide, reactive, ref, type InjectionKey, type Ref } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export type SgPreSignersCtx = {
  entityCd: Ref<string>;
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  exportAll: () => Record<string, SignerEntry[]>;
  importAll: (map: Record<string, SignerEntry[]> | null | undefined) => void;
  seedFromValuesIfEmpty: (values: Record<string, any>) => void;
};

export const SG_PRE_SIGNERS: InjectionKey<SgPreSignersCtx> = Symbol('singbacPostSigners');

/** DRIW 이사회 결의서 서명 블록의 [[Signers]] varKey */
export const SG_PRE_SIGNER_KEYS = {
  driw: 'driw_Signers',
} as const;

const SG_PRE_SIGNER_DOC_KEYS = [SG_PRE_SIGNER_KEYS.driw] as const;

/**
 * DRIW 이사회 = 회사의 이사들. Singbac 구조에서 알려진 이사는
 *   · client(오너/이사) · resident director(Singbac 지명 이사) 이므로 둘을 시드한다.
 */
export function buildSgPreSignersFromValues(values: Record<string, any>): SignerEntry[] {
  const out: SignerEntry[] = [];
  const src = values && typeof values === 'object' ? values : {};
  const push = (id: string, name: unknown, title: string) => {
    const nm = String(name ?? '').trim();
    if (!nm) return;
    out.push({
      id,
      name: nm,
      title,
      role: 'director',
      isManual: true,
      showTitle: true,
      _signerLayout: 'box',
    });
  };
  push('singbac-dir-client', src.client_name, 'Director');
  push('singbac-dir-resident', src.resident_director_name, 'Director');
  return out;
}

export function provideSgPreSigners(_entityCdIgnored?: Ref<string>): SgPreSignersCtx {
  const store = reactive<Record<string, SignerEntry[]>>({});
  const signerEntityCd = ref('');

  const ctx: SgPreSignersCtx = {
    entityCd: signerEntityCd,
    signersOf: (varKey: string) => store[varKey] || [],
    setSigners: (varKey: string, list: SignerEntry[]) => {
      store[varKey] = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
    },
    exportAll: () => {
      const out: Record<string, SignerEntry[]> = {};
      for (const [k, v] of Object.entries(store)) {
        if (v?.length) out[k] = v.map((s) => ({ ...s }));
      }
      return out;
    },
    importAll: (map) => {
      for (const k of Object.keys(store)) delete store[k];
      if (!map || typeof map !== 'object') return;
      for (const [k, v] of Object.entries(map)) {
        if (Array.isArray(v)) store[k] = v.map((s) => ({ ...s }));
      }
    },
    seedFromValuesIfEmpty: (values) => {
      const defaults = buildSgPreSignersFromValues(values);
      if (!defaults.length) return;
      for (const key of SG_PRE_SIGNER_DOC_KEYS) {
        if (!store[key]?.length) store[key] = defaults.map((s) => ({ ...s }));
      }
    },
  };

  provide(SG_PRE_SIGNERS, ctx);
  return ctx;
}

export function useSgPreSigners(): SgPreSignersCtx {
  const ctx = inject(SG_PRE_SIGNERS, null);
  if (!ctx) {
    const emptyCd = ref('');
    return {
      entityCd: emptyCd,
      signersOf: () => [],
      setSigners: () => {},
      exportAll: () => ({}),
      importAll: () => {},
      seedFromValuesIfEmpty: () => {},
    };
  }
  return ctx;
}
